import { handleActions } from "redux-actions";
import me from "models/me";
import type { IProductColorInsight } from "types/insights/product-color-insight";
import type { IProductSizeInsights } from "types/insights/product-size-insight";
import type { IBadges } from "types/insights/product-badges";
import { INSIGHTS } from "constants/action-types";
import type { ProductStyleInsights } from "models/insights/product-style-insights";
import type { CategoryInsights } from "models/insights/category-insights";
import type { IStore } from "reducers";

export const isCurrencyCodeUSDSelector = (state: IStore) => {
    return state?.scope?.currencyCode === "USD";
};

export const isCompanyCurrencyCodeUSDSelector = (state: IStore) => {
    return state?.scope?.company?.__currency_codes.includes("USD");
};

export const isLoadingInsightsSelector = (state: IStore, pageOrPathname: string): boolean => {
    const baseInsightsLoading =
        state.insights.isLoading.productStyles ||
        state.insights.isLoading.colors ||
        state.insights.isLoading.categories;

    if (pageOrPathname.includes("cart")) {
        return (
            baseInsightsLoading ||
            !!state.working.products.isFetching ||
            (state.insights.isLoading.productSizes && Object.keys(state.insights.productSizes)?.length === 0)
        );
    } else {
        return baseInsightsLoading;
    }
};

// check feature flags to ensure data has been requested, otherwise an eternal spinner can be shown.
export const isFirstLoadInsightsSelector = (state: IStore, pageOrPathname: string): boolean => {
    return (
        (state.ldFlags.productInsightsNu67199 && state.insights.isFirstLoad.productStyles) ||
        (state.ldFlags.categoryInsightsNu67199 && state.insights.isFirstLoad.categories) ||
        (state.ldFlags.colorInsightsNu67199 && state.insights.isFirstLoad.colors) ||
        (state.ldFlags.sizeInsightsNu67199 &&
            pageOrPathname.includes("cart") &&
            state.cart.items.length > 0 &&
            state.insights.isFirstLoad.productSizes)
    );
};

export const isInsightsButtonEnabledSelector = (state: IStore, pageOrPathname: "gallery" | "cart" | string) => {
    const isHideProductTabForBuyerEnabled =
        state.scope.brand?.hasPreference("hide_products_tab_for_buyer") && me.isBuyer();

    const isNotLoading =
        !state.insights.isLoading.productStyles &&
        !state.insights.isLoading.colors &&
        !state.insights.isLoading.categories;

    const hasProductStyles =
        state.insights.productStyles?.immediate?.length > 0 || state.insights.productStyles?.prebook?.length > 0;
    const hasProductSizes = Object.keys(state.insights.productSizes)?.length > 0;
    const hasProductColors = state.insights.productColors?.length > 0;
    const hasCategories = state.insights.categories?.length > 0;

    /**
     * If we are in the cart page and hide_products_tab_for_buyer is enabled,
     * and if there is only data for the product styles,
     * then the insights button should be disabled.
     */
    if (
        isNotLoading &&
        pageOrPathname.includes("cart") &&
        isHideProductTabForBuyerEnabled &&
        !hasProductSizes &&
        !hasProductColors &&
        !hasCategories
    ) {
        return false;
    }

    return (
        isCurrencyCodeUSDSelector(state) &&
        isNotLoading &&
        (hasProductStyles || (pageOrPathname.includes("cart") && hasProductSizes) || hasProductColors || hasCategories)
    );
};

// The pathname is passed in from the component's props because it cannot be accessed directly from the store.
// see https://github.com/reactjs/react-router-redux?tab=readme-ov-file#how-do-i-access-router-state-in-a-container-component
export const isInsightsPaneStateSavedAsOpenSelector = (state: IStore, pageOrPathname: string) => {
    if (pageOrPathname.includes("gallery")) {
        return state.insights.galleryInsightsSidePanelOpen;
    } else if (pageOrPathname.includes("cart")) {
        return state.insights.cartInsightsSidePanelOpen;
    } else {
        return false;
    }
};

export const isInsightsPanelOpenedSelector = (state: IStore, pageOrPathname: "gallery" | "cart" | string) => {
    return (
        isInsightsButtonEnabledSelector(state, pageOrPathname) &&
        isInsightsPaneStateSavedAsOpenSelector(state, pageOrPathname)
    );
};

type ILoadingState = {
    categories: boolean;
    colors: boolean;
    badges: boolean;
    productStyles: boolean;
    productSizes: boolean;
};

type isFirstLoad = {
    categories: boolean;
    colors: boolean;
    productStyles: boolean;
    productSizes: boolean;
};

type ProductStyles = {
    immediate?: ProductStyleInsights[];
    prebook?: ProductStyleInsights[];
};

export interface IInsightsState {
    productStyles: ProductStyles;
    categories: CategoryInsights[];
    badges: IBadges;
    cartInsightsSidePanelOpen: boolean;
    galleryInsightsSidePanelOpen: boolean;
    productColors: IProductColorInsight[] | null;
    productSizes: IProductSizeInsights;
    isFirstLoad: isFirstLoad;
    isLoading: ILoadingState;
    cartUpdating: boolean;
}

export const insights = handleActions<IInsightsState>(
    {
        [INSIGHTS.FETCH_PRODUCT_STYLE_INSIGHTS.START]: (state: IInsightsState) => {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    productStyles: true,
                },
            };
        },
        [INSIGHTS.FETCH_CATEGORY_INSIGHTS.START]: (state: IInsightsState) => {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    categories: true,
                },
            };
        },
        [INSIGHTS.FETCH_PRODUCT_COLOR_INSIGHTS.START]: (state: IInsightsState) => {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    colors: true,
                },
            };
        },
        [INSIGHTS.FETCH_BADGES.START]: (state: IInsightsState) => {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    badges: true,
                },
            };
        },
        [INSIGHTS.FETCH_PRODUCT_SIZE_INSIGHTS.START]: (state: IInsightsState) => {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    productSizes: true,
                },
            };
        },
        [INSIGHTS.FETCH_PRODUCT_STYLE_INSIGHTS.COMPLETED]: (state: IInsightsState, { productStyles }: any) => {
            return {
                ...state,
                productStyles,
                isFirstLoad: {
                    ...state.isFirstLoad,
                    productStyles: false,
                },
                isLoading: {
                    ...state.isLoading,
                    productStyles: false,
                },
            };
        },
        [INSIGHTS.FETCH_PRODUCT_STYLE_INSIGHTS.ERROR]: (state: IInsightsState) => {
            return {
                ...state,
                isFirstLoad: {
                    ...state.isFirstLoad,
                    productStyles: false,
                },
                isLoading: {
                    ...state.isLoading,
                    productStyles: false,
                },
            };
        },
        [INSIGHTS.FETCH_CATEGORY_INSIGHTS.COMPLETED]: (state: IInsightsState, { categories }: any) => {
            return {
                ...state,
                categories,
                isFirstLoad: {
                    ...state.isFirstLoad,
                    categories: false,
                },
                isLoading: {
                    ...state.isLoading,
                    categories: false,
                },
            };
        },
        [INSIGHTS.FETCH_CATEGORY_INSIGHTS.ERROR]: (state: IInsightsState) => {
            return {
                ...state,
                isFirstLoad: {
                    ...state.isFirstLoad,
                    categories: false,
                },
                isLoading: {
                    ...state.isLoading,
                    categories: false,
                },
            };
        },
        [INSIGHTS.FETCH_BADGES.COMPLETED]: (state: IInsightsState, { badges }: any) => {
            return {
                ...state,
                badges: badges,
                isLoading: {
                    ...state.isLoading,
                    badges: false,
                },
            };
        },
        [INSIGHTS.FETCH_BADGES.ERROR]: (state: IInsightsState) => {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    badges: false,
                },
            };
        },
        [INSIGHTS.CART_SIDE_PANEL_TOGGLE_OPEN]: (state) => {
            me?.setOrderTrendsPanelOpen?.(true);
            return {
                ...state,
                cartInsightsSidePanelOpen: true,
            };
        },
        [INSIGHTS.CART_SIDE_PANEL_TOGGLE]: (state) => {
            me.setOrderTrendsPanelOpen(!state.cartInsightsSidePanelOpen);
            return {
                ...state,
                cartInsightsSidePanelOpen: !state.cartInsightsSidePanelOpen,
            };
        },
        [INSIGHTS.GALLERY_SIDE_PANEL_TOGGLE]: (state) => {
            return {
                ...state,
                galleryInsightsSidePanelOpen: !state.galleryInsightsSidePanelOpen,
            };
        },
        [INSIGHTS.GALLERY_SIDE_PANEL_CLOSE]: (state) => {
            return {
                ...state,
                galleryInsightsSidePanelOpen: false,
            };
        },
        [INSIGHTS.FETCH_PRODUCT_COLOR_INSIGHTS.COMPLETED]: (state: IInsightsState, { productColors }: any) => {
            return {
                ...state,
                productColors,
                isFirstLoad: {
                    ...state.isFirstLoad,
                    colors: false,
                },
                isLoading: {
                    ...state.isLoading,
                    colors: false,
                },
            };
        },
        [INSIGHTS.FETCH_PRODUCT_COLOR_INSIGHTS.ERROR]: (state: IInsightsState) => {
            return {
                ...state,
                isFirstLoad: {
                    ...state.isFirstLoad,
                    colors: false,
                },
                isLoading: {
                    ...state.isLoading,
                    colors: false,
                },
            };
        },
        [INSIGHTS.FETCH_PRODUCT_SIZE_INSIGHTS.COMPLETED]: (state: IInsightsState, { productSizes }: any) => {
            return {
                ...state,
                productSizes: {
                    ...state.productSizes,
                    ...productSizes,
                },
                isFirstLoad: {
                    ...state.isFirstLoad,
                    productSizes: false,
                },
                isLoading: {
                    ...state.isLoading,
                    productSizes: false,
                },
            };
        },
        [INSIGHTS.FETCH_PRODUCT_SIZE_INSIGHTS.ERROR]: (state: IInsightsState) => {
            return {
                ...state,
                isFirstLoad: {
                    ...state.isFirstLoad,
                    productSizes: false,
                },
                isLoading: {
                    ...state.isLoading,
                    productSizes: false,
                },
            };
        },
        [INSIGHTS.FETCH_PRODUCT_SIZE_INSIGHTS.FETCH_DISABLED]: (state: IInsightsState) => {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    productSizes: false,
                },
            };
        },
        [INSIGHTS.CLEAR]: (state: IInsightsState) => {
            return {
                ...state,
                productStyles: {},
                categories: [],
                badges: undefined,
                productColors: null,
                productSizes: {},
                isFirstLoad: {
                    categories: true,
                    colors: true,
                    badges: true,
                    productStyles: true,
                    productSizes: true,
                },
            };
        },
    },
    {
        productStyles: {},
        categories: [],
        badges: undefined,
        // ?.() here to allow tests with me mocked to pass
        cartInsightsSidePanelOpen: me?.getIsOrderTrendsPanelOpen?.() || false,
        galleryInsightsSidePanelOpen: true,
        productColors: null,
        productSizes: {},
        isFirstLoad: {
            categories: true,
            colors: true,
            productStyles: true,
            productSizes: true,
        },
        isLoading: {
            categories: false,
            colors: false,
            badges: false,
            productStyles: false,
            productSizes: false,
        },
        cartUpdating: false,
    },
);
